import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Menu, MenuItem, Label, PortalWrapper } from 'mw-style-react';
import { useIntl } from 'hooks';
import {
  ADD_NOTIFICATION_SETTINGS,
  DELETE_NOTIFICATION_SETTINGS,
  SNOOZE_TIMES,
} from '@control-front-end/common/constants/notifications';
import cn from 'classnames';
import './SnoozeMenu.scss';
import mes from './intl';

function SnoozeMenu({
  objType,
  objId,
  bindingElement,
  onClose,
  isMutedActor = false,
  opened = null,
  triggerElement,
}) {
  const t = useIntl();
  const dispatch = useDispatch();
  const triggerRef = useRef(null);
  const [menu, toggleMenu] = useState(false);
  const handleSnooze = useCallback(
    (snooze) => {
      dispatch({
        type: ADD_NOTIFICATION_SETTINGS.REQUEST,
        payload: { type: objType, objId, snooze },
      });
    },
    [objId, objType]
  );

  useEffect(() => {
    toggleMenu(opened);
  }, [opened]);

  const handleUnmute = useCallback(() => {
    dispatch({
      type: DELETE_NOTIFICATION_SETTINGS.REQUEST,
      payload: { type: objType, objId },
    });
  }, [objId, objType]);

  const closeMenu = () => {
    onClose?.();
    toggleMenu(false);
  };

  const handleMenuClick = ({ value }) => {
    switch (value) {
      case 'forever':
        if (isMutedActor) {
          handleUnmute();
        } else {
          handleSnooze(SNOOZE_TIMES.FOREVER);
        }
        break;
      case '1h':
        handleSnooze(SNOOZE_TIMES.ONE_HOUR);
        break;
      case '3h':
        handleSnooze(SNOOZE_TIMES.THREE_HOURS);
        break;
      case '24h':
        handleSnooze(SNOOZE_TIMES.ONE_DAY);
        break;
      default:
        break;
    }
    closeMenu();
  };

  const renderMenu = () => {
    const visibility = isMutedActor ? 'hidden' : 'visible';
    return (
      <PortalWrapper
        root={document.getElementById('mainRoot')}
        node={bindingElement ?? triggerRef?.current}
        vAlign="top"
        hAlign="right"
        offsetY={32}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Menu
            size="small"
            onClick={({ value }) => handleMenuClick({ value })}
            onClose={closeMenu}
          >
            <MenuItem
              size="small"
              leftIcon={isMutedActor ? 'notification' : 'no_notification'}
              label={isMutedActor ? t(mes.unmute) : t(mes.muteForever)}
              styleName={cn({ item__mute: !isMutedActor })}
              value="forever"
            />
            <Label
              size="small"
              value={t(mes.snooze)}
              fontSize={Label.SIZES.small}
              color={Label.COLOR.black}
              styleName="item__snooze"
              visibility={visibility}
            />
            {['1h', '3h', '24h'].map((duration) => (
              <MenuItem
                key={duration}
                size="small"
                label={duration}
                value={duration}
                visibility={visibility}
              />
            ))}
          </Menu>
        </div>
      </PortalWrapper>
    );
  };

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
          toggleMenu(!menu);
        }}
      >
        <div ref={triggerRef}>{triggerElement}</div>
      </div>
      {menu ? renderMenu() : null}
    </>
  );
}

export default SnoozeMenu;
